const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// In-memory token store with sessionStorage persistence
let _memoryToken = null;

const getToken = () => {
  if (_memoryToken) return _memoryToken;
  try {
    const stored = localStorage.getItem('qplan_tk');
    if (stored) {
      _memoryToken = stored;
      return stored;
    }
  } catch {}
  return null;
};

export const setToken = (token) => {
  _memoryToken = token || null;
  try {
    if (token) localStorage.setItem('qplan_tk', token);
    else localStorage.removeItem('qplan_tk');
  } catch {}
};

export const getStoredUser = () => {
  try {
    const user = localStorage.getItem('qplan_user');
    return user ? JSON.parse(user) : null;
  } catch { return null; }
};

export const setStoredUser = (user) => {
  if (user) localStorage.setItem('qplan_user', JSON.stringify(user));
  else localStorage.removeItem('qplan_user');
};

// ==================== OFFLINE CACHE ====================
const OFFLINE_CACHE_KEY = 'qplan_offline_cache';

function getCacheKey(endpoint) {
  return `${OFFLINE_CACHE_KEY}:${endpoint}`;
}

function setOfflineCache(endpoint, data) {
  try {
    localStorage.setItem(getCacheKey(endpoint), JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch {}
}

function getOfflineCache(endpoint) {
  try {
    const raw = localStorage.getItem(getCacheKey(endpoint));
    if (!raw) return null;
    const { data } = JSON.parse(raw);
    return data;
  } catch { return null; }
}

function invalidateCache(pattern) {
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith(OFFLINE_CACHE_KEY) && (!pattern || key.includes(pattern))) {
        localStorage.removeItem(key);
      }
    }
  } catch {}
}

// ==================== OFFLINE QUEUE ====================
const OFFLINE_QUEUE_KEY = 'qplan_offline_queue';

function getOfflineQueue() {
  try {
    return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]');
  } catch { return []; }
}

function addToOfflineQueue(endpoint, method, body) {
  try {
    const queue = getOfflineQueue();
    queue.push({ endpoint, method, body, timestamp: Date.now() });
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
  } catch {}
}

export async function processOfflineQueue() {
  const queue = getOfflineQueue();
  if (queue.length === 0) return;
  const remaining = [];
  for (const item of queue) {
    try {
      await apiCall(item.endpoint, {
        method: item.method,
        body: item.body ? JSON.stringify(item.body) : undefined,
      }, 1);
    } catch {
      remaining.push(item);
    }
  }
  try {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(remaining));
  } catch {}
}

// ==================== NETWORK STATUS ====================
let _isOnline = navigator.onLine;
const _listeners = new Set();

export function onNetworkChange(callback) {
  _listeners.add(callback);
  return () => _listeners.delete(callback);
}

export function isOnline() { return _isOnline; }

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    _isOnline = true;
    _listeners.forEach(cb => cb(true));
    processOfflineQueue();
  });
  window.addEventListener('offline', () => {
    _isOnline = false;
    _listeners.forEach(cb => cb(false));
  });
}

// ==================== API CALLS ====================

export async function apiCall(endpoint, options = {}, retries = MAX_RETRIES) {
  const token = getToken();
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_URL}${endpoint}`;
  
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok || (response.status >= 400 && response.status < 500)) {
        return response;
      }
      
      if ([502, 503, 504].includes(response.status) && attempt < retries) {
        await delay(RETRY_DELAY);
        continue;
      }
      
      return response;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('La solicitud tardo demasiado. Verifica tu conexion.');
      }
      if (attempt === retries) {
        throw new Error('Error de conexion. Verifica tu conexion a internet.');
      }
      await delay(RETRY_DELAY);
    }
  }
  
  throw new Error('No se pudo conectar con el servidor');
}

export async function get(endpoint) {
  // Always try network first
  if (navigator.onLine) {
    const response = await apiCall(endpoint);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = typeof errorData.detail === 'string' ? errorData.detail 
        : Array.isArray(errorData.detail) ? errorData.detail.map(e => e.msg || e).join('. ')
        : errorData.message || `Error ${response.status}`;
      throw new Error(errorMessage);
    }
    const data = await response.json();
    setOfflineCache(endpoint, data);
    return data;
  }
  // Offline: serve from cache
  const cached = getOfflineCache(endpoint);
  if (cached) {
    cached._fromCache = true;
    return cached;
  }
  throw new Error('Sin conexion y sin datos guardados');
}

export async function post(endpoint, body) {
  if (!navigator.onLine) {
    addToOfflineQueue(endpoint, 'POST', body);
    return { _queued: true, message: 'Accion guardada. Se enviara al volver la conexion.' };
  }
  const response = await apiCall(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = typeof errorData.detail === 'string' ? errorData.detail 
      : Array.isArray(errorData.detail) ? errorData.detail.map(e => e.msg || e).join('. ')
      : errorData.message || `Error ${response.status}`;
    throw new Error(errorMessage);
  }
  invalidateCache();
  return response.json();
}

export async function put(endpoint, body) {
  const response = await apiCall(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = typeof errorData.detail === 'string' ? errorData.detail 
      : Array.isArray(errorData.detail) ? errorData.detail.map(e => e.msg || e).join('. ')
      : errorData.message || `Error ${response.status}`;
    throw new Error(errorMessage);
  }
  invalidateCache();
  return response.json();
}

export async function del(endpoint) {
  const response = await apiCall(endpoint, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = typeof errorData.detail === 'string' ? errorData.detail 
      : Array.isArray(errorData.detail) ? errorData.detail.map(e => e.msg || e).join('. ')
      : errorData.message || `Error ${response.status}`;
    throw new Error(errorMessage);
  }
  invalidateCache();
  return response.json();
}

export { API_URL };
